/**
 * Tile types, glyph mapping, and movement/sight predicates for the world grid.
 * Purely headless — no PixiJS. CLAUDE.md architecture rule 4.
 */

export const enum TileType {
  Void = 0,
  Floor = 1,
  Wall = 2,
  /**
   * Cubicle partition. Renders with a directional box-drawing glyph
   * (`═`, `║`, `╬`, corners) derived from partition neighbors — the glyph is
   * baked into `MapData.glyphs` at map construction so rendering stays dumb.
   */
  CubiclePartition = 3,
  /** An OPEN door — passable, see-through, lets sound through (soundCostKey
   *  'door'). Toggling with E swaps it to `DoorClosed` and back (see
   *  `toggleDoor`). Existing map doorways are placed as open passages. */
  Door = 4,
  AmmoPickup = 5,
  HealStation = 6,
  /**
   * Half wall: waist-high cover. Blocks movement always. Blocks vision
   * for **crouched** observers (they can't see over it). Standing observers
   * see over the top but the tile *directly behind* the half wall (relative
   * to their line of sight) is shadowed → enemies crouched there stay
   * hidden. Rendered as ▄ (lower half block).
   */
  HalfWall = 7,
  /**
   * Wall-mounted fusebox / breaker panel. Solid (blocks move + sight + bullets)
   * like the wall it sits in; the player stands adjacent and presses E to open
   * the floor's power controls (room lights for now; cameras/turrets/doors
   * later). Rendered as ▦, electric-yellow.
   */
  Fusebox = 8,
  /**
   * Window in an exterior/end wall. Solid (blocks movement + bullets) but glass:
   * light passes THROUGH it (it doesn't occlude light), so it admits sunlight
   * from outside. See-through for vision too. Rendered as ▒, sky-warm.
   */
  Window = 9,
  /**
   * A CLOSED door. Wall-like: blocks movement, sight, bullets, and sound
   * (soundCostKey 'wall') — opaque like a wall while shut. E toggles it to
   * `Door` (open) when its lock, if any, is not engaged. Its per-door metadata
   * (kind, lock) lives in `MapData.doors`.
   */
  DoorClosed = 10,

  // --- Furniture / prop ARCHETYPES (procedural generation, Phase 1+) ---
  // Props are placed by ENGINE BEHAVIOR (these four archetypes) and get their
  // specific identity — desk vs. cabinet, faucet vs. breaker — from a per-tile
  // glyph override + prop-metadata record, not a tile type per object. Keeps the
  // enum lean while allowing unlimited prop variety (mirrors the janitor-cabinet
  // pattern: a solid tile + overridden glyph + a Container in MapData).
  /** Waist-high see-over furniture cover: desks, tables, counters, low cabinets.
   *  Blocks move + bullets, see-over (a standing observer looks over it, a
   *  crouched one is blocked) — same silhouette as a half wall. */
  PropLow = 11,
  /** Solid, sight- and sound-blocking furniture: server racks, shelving,
   *  lockers, big cabinets, safes. Wall-like but a destructible-later prop. */
  PropTall = 12,
  /** Passable decor: potted plants, chairs, small clutter. Walk through/around;
   *  no cover. A distinct type so it renders a decor glyph + can carry prop
   *  metadata, unlike bare Floor. */
  PropDecor = 13,
  /** Stairs UP — walk onto it and you climb. Direction is a PLACE, not a
   *  keypress: the core carries an up-stair and a down-stair side by side on
   *  every floor, and travelling lands you on the destination's paired tile. */
  StairsUp = 14,
  /** Stairs DOWN — the other half of the pair. */
  StairsDown = 15,
}

/** Which `CONFIG.sound.cost` key governs sound crossing INTO a tile. */
export type SoundCostKey = 'floor' | 'wall' | 'cubicle' | 'door' | 'halfWall';

/**
 * Per-tile trait record — the single source of truth for what each tile type
 * *is*. Every structural predicate below reads from this table instead of
 * enumerating tile types by hand, so a new tile (or a divergent one, e.g. a
 * cubicle that's less sound-dampening and bullet-penetrable) is defined in ONE
 * place. Tunable numbers that belong to gameplay balance (sound cost values)
 * stay in `config.ts`; the table only names WHICH cost key applies. Prop
 * toughness lives here because it's part of the tile's identity.
 */
export interface TileTraits {
  /** Blocks entity movement. */
  blocksMove: boolean;
  /** Hard-opaque to a STANDING observer (full walls, Void). Cover props are
   *  see-over (a standing observer looks over them; a one-tile foot shadow sits
   *  directly behind — see `coverShadowMargin`), so they are NOT hard-opaque. */
  blocksVisionStanding: boolean;
  /** Waist/chest-high "see-over" cover: casts the foot shadow and is fully
   *  opaque to a CROUCHED observer. */
  cover: boolean;
  /** Can be taken as cover with X (full walls + cover props). */
  coverAnchor: boolean;
  /** Stops a bullet in flight. */
  stopsBullet: boolean;
  /** Sound-dampening key into `CONFIG.sound.cost`. */
  soundCostKey: SoundCostKey;
  /** If present, the prop can be shot to pieces: `hp` damage destroys it into
   *  `rubble`. Absent = indestructible. */
  destructible?: { hp: number; rubble: TileType };
}

export const TILE_TRAITS: Record<TileType, TileTraits> = {
  [TileType.Void]: {
    blocksMove: true, blocksVisionStanding: true, cover: false, coverAnchor: false,
    stopsBullet: true, soundCostKey: 'wall',
  },
  [TileType.Floor]: {
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'floor',
  },
  [TileType.Wall]: {
    blocksMove: true, blocksVisionStanding: true, cover: false, coverAnchor: true,
    stopsBullet: true, soundCostKey: 'wall',
  },
  [TileType.CubiclePartition]: {
    // Thin office panels: see-over cover, LESS sound-dampening than a half wall,
    // bullet-PENETRABLE, and destructible after a good amount of fire.
    blocksMove: true, blocksVisionStanding: false, cover: true, coverAnchor: true,
    stopsBullet: false, soundCostKey: 'cubicle',
    destructible: { hp: 80, rubble: TileType.Floor },
  },
  [TileType.Door]: {
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'door',
  },
  [TileType.AmmoPickup]: {
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'floor',
  },
  [TileType.HealStation]: {
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'floor',
  },
  [TileType.HalfWall]: {
    blocksMove: true, blocksVisionStanding: false, cover: true, coverAnchor: true,
    stopsBullet: true, soundCostKey: 'halfWall',
  },
  [TileType.Fusebox]: {
    // A solid panel in the wall — opaque to sight AND to light (blocksVisionStanding
    // doubles as the light-occlusion mask). Not cover; you interact from beside it.
    blocksMove: true, blocksVisionStanding: true, cover: false, coverAnchor: false,
    stopsBullet: true, soundCostKey: 'wall',
  },
  [TileType.Window]: {
    // Glass: solid to bodies + bullets, but transparent to sight AND to light
    // (blocksVisionStanding false → not a light occluder), so it admits sunlight.
    blocksMove: true, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: true, soundCostKey: 'wall',
  },
  [TileType.DoorClosed]: {
    // Shut door: opaque like a wall to move/sight/light/bullets. NOT to sound —
    // it's a door. It takes the 'door' cost CLAUDE.md specifies (heavy but
    // partial) and leaks a tenth of what reaches it (CONFIG.sound.transmission),
    // so you can hear someone on the other side of a shut door but not clearly.
    // (It carried the WALL cost until now, which made a door acoustically solid
    // and contradicted the documented sound model.)
    blocksMove: true, blocksVisionStanding: true, cover: false, coverAnchor: false,
    stopsBullet: true, soundCostKey: 'door',
  },
  [TileType.PropLow]: {
    // Waist-high furniture: see-over cover that stops movement + bullets, like a
    // half wall. Destructible/flip behaviour is layered on later (prop verbs).
    blocksMove: true, blocksVisionStanding: false, cover: true, coverAnchor: true,
    stopsBullet: true, soundCostKey: 'halfWall',
  },
  [TileType.PropTall]: {
    // Solid furniture (racks, shelving, lockers, safes): blocks move/sight/light/
    // bullets/sound like a wall; a cover anchor you hug against.
    blocksMove: true, blocksVisionStanding: true, cover: false, coverAnchor: true,
    stopsBullet: true, soundCostKey: 'wall',
  },
  [TileType.PropDecor]: {
    // Passable clutter/decor: no blocking, no cover — you move through it.
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'floor',
  },
  [TileType.StairsUp]: {
    // Walkable transition tile; floor-like for move/sight/sound. Standing on it
    // starts the travel channel (main.ts) — the tile itself carries no trait.
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'floor',
  },
  [TileType.StairsDown]: {
    blocksMove: false, blocksVisionStanding: false, cover: false, coverAnchor: false,
    stopsBullet: false, soundCostKey: 'floor',
  },
};

/** Whether an entity may occupy this tile. */
export function isBlocking(t: TileType): boolean {
  return TILE_TRAITS[t].blocksMove;
}

/** Hard sight-blocker for a STANDING observer (full walls + off-map Void). */
export function blocksVision(t: TileType): boolean {
  return TILE_TRAITS[t].blocksVisionStanding;
}

/** Waist/chest-high see-over cover (cubicle partitions + half walls). */
export function isCoverTile(t: TileType): boolean {
  return TILE_TRAITS[t].cover;
}

/** A tile the player can take cover against with X (walls + cover props). */
export function isCoverAnchor(t: TileType): boolean {
  return TILE_TRAITS[t].coverAnchor;
}

/** Opacity mask for a CROUCHED observer — everything `blocksVision` blocks plus
 *  all cover tiles (a crouched observer is below their top). */
export function blocksVisionCrouched(t: TileType): boolean {
  const tr = TILE_TRAITS[t];
  return tr.blocksVisionStanding || tr.cover;
}

/** True if the tile stops a bullet in flight. */
export function tileStopsBullet(t: TileType): boolean {
  return TILE_TRAITS[t].stopsBullet;
}

/** Destruction info for a destructible prop, or null if indestructible. */
export function tileDestructible(t: TileType): { hp: number; rubble: TileType } | null {
  return TILE_TRAITS[t].destructible ?? null;
}

/** Default per-tile glyph. Cubicle partitions are overridden by autotile. */
export function defaultGlyph(t: TileType): string {
  switch (t) {
    case TileType.Void:
      return ' ';
    case TileType.Floor:
      return '.';
    case TileType.Wall:
      return '█';
    case TileType.CubiclePartition:
      return '═';
    case TileType.Door:
      return '║'; // open door — same leaf as DoorClosed, rotated ajar by the renderer
    case TileType.AmmoPickup:
      return 'A';
    case TileType.HealStation:
      return 'H';
    case TileType.HalfWall:
      return '▄';
    case TileType.Fusebox:
      return '▦';
    case TileType.Window:
      return '▒';
    case TileType.DoorClosed:
      return '║'; // shut door — stacks to wall height into a framed doorway
    case TileType.PropLow:
      return '╥'; // generic table/desk — overridden per prop at placement
    case TileType.PropTall:
      return '▥'; // generic rack/cabinet — overridden per prop at placement
    case TileType.PropDecor:
      return '◊'; // generic decor/plant — overridden per prop at placement
    case TileType.StairsUp:
      return '≤'; // stairs up
    case TileType.StairsDown:
      return '≥'; // stairs down
  }
}

/** Foreground color (24-bit RGB). Tuned for a low-key, DF-ish palette. */
export function defaultFg(t: TileType): number {
  switch (t) {
    case TileType.Void:
      return 0x000000;
    case TileType.Floor:
      return 0x7c8590;
    case TileType.Wall:
      return 0x8a95a4;
    case TileType.CubiclePartition:
      return 0xb69670;
    case TileType.Door:
      return 0xd0a970;
    case TileType.DoorClosed:
      return 0xc79a5c;
    case TileType.AmmoPickup:
      return 0xe6d060;
    case TileType.HealStation:
      return 0xe07070;
    case TileType.HalfWall:
      return 0x9a8474;
    case TileType.Fusebox:
      return 0xffd23f;
    case TileType.Window:
      return 0xffe6a0;
    case TileType.PropLow:
      return 0xb08a5a; // wood/furniture
    case TileType.PropTall:
      return 0x8590a0; // metal shelving
    case TileType.PropDecor:
      return 0x6a9a6a; // planty green
    case TileType.StairsUp:
    case TileType.StairsDown:
      return 0xc0c8d0; // concrete
  }
}

/** Tile bg color. All negative — we don't paint filled background quads
 *  anymore; the tile identity comes entirely from the glyph tint. Kept in
 *  the API so map/renderer can still ask for a bg color if we ever want
 *  per-tile highlights (blood, hazard, etc.). */
export function defaultBg(_t: TileType): number {
  return -1;
}
